import React, { useMemo, useState } from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { Notes, UnfoldLess, UnfoldMore } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const COMPACT_PREVIEW_LIMIT = 420;
const FULL_PREVIEW_LIMIT = 900;

const NotesCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  background:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.04)"
      : "rgba(2, 136, 209, 0.04)",
  overflow: "hidden",
}));

const NotesHeader = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  padding: theme.spacing(1.25, 1.5),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(1.5, 2),
  },
}));

const NotesBody = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(2),
  },
}));

const NotesText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
  lineHeight: 1.65,
  overflowWrap: "anywhere",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
}));

function normalizeNotes(notes) {
  if (notes === undefined || notes === null) return "";
  return String(notes).trim();
}

function createPreview(text, limit) {
  if (text.length <= limit) return text;
  const slice = text.slice(0, limit);
  const lastBreak = Math.max(slice.lastIndexOf("\n\n"), slice.lastIndexOf(". "));
  const end = lastBreak > limit * 0.55 ? lastBreak + 1 : limit;
  return `${text.slice(0, end).trim()}...`;
}

const LeadNotesPreview = ({
  notes,
  compact = false,
  title = "Notes",
  initiallyExpanded = false,
}) => {
  const normalizedNotes = normalizeNotes(notes);
  const [expanded, setExpanded] = useState(initiallyExpanded);

  const previewLimit = compact ? COMPACT_PREVIEW_LIMIT : FULL_PREVIEW_LIMIT;
  const isLong = normalizedNotes.length > previewLimit;
  const displayText = useMemo(() => {
    if (!isLong || expanded) return normalizedNotes;
    return createPreview(normalizedNotes, previewLimit);
  }, [expanded, isLong, normalizedNotes, previewLimit]);

  if (!normalizedNotes) return null;

  return (
    <NotesCard>
      <NotesHeader>
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
          <Notes fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {isLong && (
            <Chip
              label={expanded ? "Full note" : "Preview"}
              size="small"
              color={expanded ? "primary" : "default"}
              variant={expanded ? "filled" : "outlined"}
            />
          )}
        </Stack>
        {isLong && (
          <Button
            size="small"
            onClick={() => setExpanded((value) => !value)}
            endIcon={expanded ? <UnfoldLess /> : <UnfoldMore />}
            aria-expanded={expanded}
          >
            {expanded ? "Show less" : "Show more"}
          </Button>
        )}
      </NotesHeader>
      <NotesBody
        sx={{
          maxHeight: expanded ? { xs: 420, sm: 560 } : "none",
          overflowY: expanded ? "auto" : "visible",
        }}
      >
        <NotesText variant="body2">{displayText}</NotesText>
      </NotesBody>
    </NotesCard>
  );
};

export default LeadNotesPreview;
